<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- UI: "Image Library" — a two-pane page (left rail + content) with an Upload view and a
     Storage view, replacing the stacked upload-panel + table. All upload logic, the
     dropFiles wrappers, the IMS-signed POST and ListUploadedFiles are reused as-is. -->
<template>
<div class="imglib">
  <!-- left rail -->
  <aside class="imglib-rail">
    <p class="imglib-rail-title">{{ $t('image-library') }}</p>
    <nav class="imglib-rail-nav">
      <button
        class="imglib-rail-item"
        :class="{active: activeView === 'upload'}"
        @click="activeView = 'upload'"
      >
        <span class="icon"><i class="fas fa-cloud-upload-alt"></i></span>
        <span>{{ $t('upload') }}</span>
      </button>
      <button
        class="imglib-rail-item"
        :class="{active: activeView === 'storage'}"
        @click="activeView = 'storage'"
      >
        <span class="icon"><i class="fas fa-database"></i></span>
        <span>{{ $t('storage') }}</span>
        <span v-if="fileCount !== null" class="imglib-rail-sub">{{ fileCount }}</span>
      </button>
    </nav>

    <!-- Storage-usage card. Capacity is read live from the mounted filesystem via
         PIMS /disk-usage (statvfs), so growing the VM's disk is picked up with no
         config change. Hidden if the endpoint is unavailable.

         The footer reports FREE space rather than a percentage: this is the whole
         volume, not a per-tenant quota, so "% of your quota" was simply untrue, and
         the number a user actually needs is whether the next upload fits. -->
    <div class="imglib-rail-foot" v-if="storageStats">
      <div class="usage-card">
        <div class="usage-top">
          <b>{{ $t('storage-used') }}</b>
          <span>{{ filesize(storageStats.used) }} / {{ filesize(storageStats.total) }}</span>
        </div>
        <div class="usage-bar"><div class="usage-fill" :style="{width: usagePercent + '%'}"></div></div>
        <div class="usage-foot">{{ $t('storage-free', {size: filesize(storageStats.available)}) }}</div>
      </div>
    </div>
  </aside>

  <!-- content -->
  <section class="imglib-main">
    <!-- ============================== UPLOAD ============================== -->
    <div v-show="activeView === 'upload'" class="imglib-view imglib-view-upload">
      <header class="imglib-head">
        <div>
          <h1 class="imglib-title">{{ $t('upload') }}</h1>
          <p class="imglib-subtitle">{{ $t('upload-subtitle') }}</p>
        </div>
        <button class="button is-light imglib-formats-btn" @click="formatsModalOpen = true">
          <span class="icon"><i class="fas fa-info-circle"></i></span>
          <span>{{ $t('supported-formats') }}</span>
        </button>
      </header>

      <b-message v-if="newUploadError" type="is-danger" has-icon icon-size="is-small">
        {{ $t('error-cannot-upload') }}
      </b-message>

      <div v-else class="imglib-card">
        <!-- Storage selector -->
        <div class="imglib-field">
          <label class="imglib-label">{{ $t('storage') }}</label>
          <ims-multiselect
            v-model="selectedStorage"
            :options="storages"
            label="extendedName"
            track-by="id"
            :allow-empty="false"
          >
            <template #option="{option}">
              {{ option.extendedName }}
              <template v-if="currentAccount.isDeveloper"> ({{ $t('id') }}: {{ option.id }})</template>
            </template>
          </ims-multiselect>
        </div>

        <!-- Link with project -->
        <div class="imglib-field">
          <label class="imglib-label">
            {{ $t('link-with-project') }}
            <span class="imglib-optional">· {{ $t('optional') }}</span>
          </label>
          <ims-multiselect
            v-model="selectedProjects"
            :options="projects"
            label="name"
            track-by="id"
            :multiple="true"
            :close-on-select="true"
            class="project-select"
          />
          <p class="imglib-hint">{{ $t('link-to-project-upload-info') }}</p>
        </div>

        <hr class="imglib-divider">

        <!-- Dropzone -->
        <div class="imglib-field">
          <label class="imglib-label">{{ $t('files') }}</label>
          <b-upload
            :value="plainFiles"
            multiple
            drag-drop
            expanded
            @input="filesChange"
            class="imglib-dropzone"
          >
            <div class="imglib-drop-inner">
              <span class="imglib-drop-icon"><i class="fas fa-cloud-upload-alt"></i></span>
              <p class="imglib-drop-title">
                {{ $t('drop-slides-here') }} <a @click.prevent>{{ $t('browse-files') }}</a>
              </p>
              <p class="imglib-drop-hint">{{ $t('upload-dropzone-hint') }}</p>
            </div>
          </b-upload>

          <!-- queued / uploading files -->
          <ul v-if="dropFiles.length > 0" class="imglib-files">
            <li v-for="(wrapper, idx) in dropFiles" :key="idx" class="imglib-file">
              <span class="imglib-file-icon"><i class="fas fa-file-image"></i></span>
              <div class="imglib-file-body">
                <div class="imglib-file-row">
                  <span class="imglib-file-name" :title="wrapper.file.name">{{ wrapper.file.name }}</span>
                  <span class="imglib-file-size">{{ filesize(wrapper.file.size) }}</span>
                </div>
                <progress
                  v-if="wrapper.uploadedFile === null"
                  class="progress is-link imglib-file-progress"
                  :value="wrapper.progress"
                  max="100"
                >{{ wrapper.progress }}%</progress>
                <!-- Absolute bytes matter at this scale: one percent of a 100GB slide is ~1GB,
                     so a percentage alone makes a healthy upload look frozen. -->
                <p v-if="wrapper.uploadedFile === null && wrapper.uploading" class="imglib-file-bytes">
                  {{ filesize(wrapper.sent || 0) }} / {{ filesize(wrapper.file.size) }}
                  <span v-if="wrapper.resumable"> · {{ $t('upload-resumed') }}</span>
                </p>
                <p v-if="wrapper.uploadedFile === false && wrapper.error" class="imglib-file-bytes has-text-danger">
                  {{ wrapper.error }}
                </p>
                <div v-else class="imglib-file-status">
                  <uploaded-file-status v-if="wrapper.uploadedFile" :file="wrapper.uploadedFile" />
                  <span v-else class="tag is-danger">{{ $t('upload-error') }}</span>
                </div>
              </div>
              <div class="imglib-file-actions">
                <button
                  v-if="wrapper.uploadedFile === null && !wrapper.uploading"
                  class="button is-link is-small"
                  @click="startUpload(wrapper)"
                >{{ $t('button-start') }}</button>
                <button
                  class="button is-small is-text"
                  @click="cancelUpload(idx)"
                >{{ wrapper.uploadedFile === null ? $t('button-cancel') : $t('button-hide') }}</button>
              </div>
            </li>
          </ul>
        </div>

        <progress
          v-if="ongoingUpload"
          class="progress is-success imglib-overall"
          :value="overallProgress"
          max="100"
        >{{ overallProgress }}%</progress>

        <!-- action bar -->
        <div class="imglib-actions">
          <b-upload :value="plainFiles" multiple @input="filesChange">
            <a class="button">
              <span class="icon"><i class="fas fa-plus"></i></span>
              <span>{{ $t('add-files') }}</span>
            </a>
          </b-upload>
          <button class="button is-link" @click="startAll()" :disabled="!filesPendingUpload">
            <span class="icon"><i class="fas fa-cloud-upload-alt"></i></span>
            <span>{{ $t('start-upload') }}</span>
          </button>
          <button class="button is-text" @click="cancelAll()" :disabled="!filesPendingUpload && !ongoingUpload">
            {{ $t('cancel-upload') }}
          </button>
          <button class="button is-text" @click="hideFinished()" v-if="filesFinishedUpload">
            {{ $t('hide-successful-upload') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============================== STORAGE ============================== -->
    <div v-show="activeView === 'storage'" class="imglib-view imglib-view-storage">
      <header class="imglib-head">
        <div>
          <h1 class="imglib-title">{{ $t('storage') }}</h1>
          <p class="imglib-subtitle" v-if="selectedStorage">
            {{ $t('all-images-in-storage', {storage: selectedStorage.name}) }}
          </p>
        </div>
      </header>
      <list-uploaded-files
        :tableRefreshInterval="tableRefreshInterval"
        :revision.sync="revision"
        embedded
        @total="fileCount = $event"
      />
    </div>
  </section>

  <!-- Supported formats modal -->
  <b-modal :active.sync="formatsModalOpen" has-modal-card>
    <div class="modal-card imglib-formats-card">
      <header class="imglib-formats-head">
        <span class="imglib-formats-ico"><i class="fas fa-info-circle"></i></span>
        <button type="button" class="delete" @click="formatsModalOpen = false"></button>
      </header>
      <section class="modal-card-body">
        <h2 class="imglib-formats-title">{{ $t('supported-image-formats') }}</h2>
        <p class="imglib-formats-desc">{{ $t('supported-formats-desc') }}</p>
        <div class="imglib-format-tags" v-if="formatInfos.length">
          <span
            v-for="format in formatInfos"
            :key="format.id"
            class="imglib-format-tag"
            :title="format.remarks"
          >{{ format.name }}</span>
        </div>
        <ul class="imglib-formats-notes">
          <li>
            <span class="icon"><i class="fas fa-cloud-upload-alt"></i></span>
            {{ $t('drag-drop-upload-info', {labelButton: $t('add-files')}) }}
          </li>
          <li>
            <span class="icon"><i class="fas fa-link"></i></span>
            {{ $t('link-after-upload-note') }}
          </li>
        </ul>
      </section>
    </div>
  </b-modal>
</div>
</template>

<script>
import axios from 'axios';
import filesize from 'filesize';
import * as tus from 'tus-js-client';

import {
  Ims,
  StorageCollection,
  ProjectCollection,
  UploadedFile,
  UploadedFileStatus,
} from '@/api';

import {get} from '@/utils/store-helpers';
import constants from '@/utils/constants.js';
import pipelineService from '@/api/pipeline-service';
import {updateToken} from '@/utils/token-utils';

import ImsMultiselect from '@/components/form/ImsMultiselect';
import ListUploadedFiles from '@/components/storage/ListUploadedFiles';
import UploadedFileStatusComponent from '@/components/storage/UploadedFileStatus';

export default {
  name: 'ims-storage',
  components: {
    ListUploadedFiles,
    ImsMultiselect,
    'uploaded-file-status': UploadedFileStatusComponent,
  },
  data() {
    return {
      // 'upload' | 'storage' — which pane the left rail shows.
      activeView: 'upload',
      // supported-formats modal + file-count badge (forwarded from the table).
      formatsModalOpen: false,
      fileCount: null,
      timeoutRefreshStorageStats: null,
      // storage usage ({used, total, usedP} in bytes/fraction); null until fetched.
      storageStats: null,

      newUploadError: false,
      timeoutRefreshSessionUploads: null,
      tableRefreshInterval: constants.STORAGE_REFRESH_INTERVAL,

      storages: [],
      selectedStorage: null,
      projects: [],
      selectedProjects: [],
      formatInfos: [],

      dropFiles: [],

      revision: 0
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    shortTermToken: get('currentUser/shortTermToken'),
    finishedStatus() {
      return [
        UploadedFileStatus.CONVERTED,
        UploadedFileStatus.DEPLOYED
      ];
    },
    ongoingUpload() {
      return this.dropFiles.some(wrapper => wrapper.uploading);
    },
    filesPendingUpload() {
      return this.dropFiles.some(wrapper => !wrapper.uploading && wrapper.uploadedFile === null);
    },
    filesFinishedUpload() {
      return this.dropFiles.some(wrapper => !wrapper.uploading && wrapper.uploadedFile !== null
        && this.finishedStatus.includes(wrapper.uploadedFile.status));
    },
    overallProgress() {
      let nbUploads = 0;
      let totalProgress = 0;
      this.dropFiles.forEach(wrapper => {
        if (wrapper.uploading) {
          nbUploads++;
          totalProgress += wrapper.progress;
        }
      });
      return Math.floor(totalProgress / nbUploads);
    },
    uri() {
      return constants.PIMS_URL + '/upload';
    },
    plainFiles() {
      return this.dropFiles.map(wrapper => wrapper.file);
    },
    usagePercent() {
      if (!this.storageStats) {
        return 0;
      }
      const p = this.storageStats.usedP != null
        ? this.storageStats.usedP * 100
        : (this.storageStats.used / this.storageStats.total) * 100;
      return Math.min(100, Math.max(0, Math.round(p)));
    },
  },
  methods: {
    async fetchStorages() {
      try {
        this.storages = (await StorageCollection.fetchAll()).array;
        this.storages.forEach(v => {
          v.extendedName = v.name;
          if (this.currentAccount.isDeveloper) {
            v.extendedName += ' ' + this.$t('id') + ': ' + v.id;
          }
        });

        this.selectedStorage = this.storages.find(storage => storage.user === this.currentUser.id);
      } catch (error) {
        console.log(error);
        this.newUploadError = true;
      }
    },
    async fetchProjects() {
      try {
        this.projects = (await ProjectCollection.fetchAll()).array;
      } catch (error) {
        console.log(error); // not mandatory for upload => only log error, no other action
      }
    },
    async fetchFormatInfos() {
      try {
        this.formatInfos = (await Ims.instance.api.get('imageserver/format.json')).data.collection;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchStorageStats() {
      // Capacity comes from PIMS, NOT from Core's stats/imageserver/total.json: that
      // endpoint is admin-only and returns 403 for an ordinary user, so the card was
      // silently hidden for everyone who is not a Cytomine admin.
      //
      // PIMS /disk-usage reports the mounted filesystem via statvfs, so the total is
      // discovered live -- grow the VM's disk and the figure follows with no config
      // change. Only the mount_* fields are read; `used_size` in the same payload is a
      // recursive directory walk and is deliberately ignored here.
      // On failure the usage card stays hidden (storageStats === null).
      try {
        const token = await updateToken();
        const {data} = await axios.get(`${constants.PIMS_URL}/disk-usage`, {
          headers: token ? {Authorization: `Bearer ${token}`} : {},
        });
        const total = Number(data.mount_total_size);
        const used = Number(data.mount_used_size);
        this.storageStats = (total > 0)
          ? {total, used, available: Number(data.mount_available_size), usedP: used / total}
          : null;
      } catch (error) {
        this.storageStats = null;
      }
    },

    // Self-rescheduling so the gauge keeps up while a multi-hour upload lands; the
    // previous code fetched once in activated() and then went stale.
    async refreshStorageStats() {
      await this.fetchStorageStats();
      this.timeoutRefreshStorageStats = setTimeout(
        this.refreshStorageStats, constants.STORAGE_USAGE_REFRESH_INTERVAL
      );
    },

    async refreshStatusSessionUploads() {
      let pendingStatus = [
        UploadedFileStatus.UPLOADED,
        UploadedFileStatus.DETECTING_FORMAT,
        UploadedFileStatus.EXTRACTING_DATA,
        UploadedFileStatus.CONVERTING,
        UploadedFileStatus.DEPLOYING,
        50,
        60
      ];

      let unfinishedConversions = false;
      let statusChange = false;

      try {
        await Promise.all(this.dropFiles.map(async wrapper => {
          if (wrapper.uploadedFile) {
            let oldStatus = wrapper.uploadedFile.status;
            if (!pendingStatus.includes(oldStatus)) {
              return;
            }

            await wrapper.uploadedFile.fetch();
            let status = wrapper.uploadedFile.status;
            if (status !== oldStatus) {
              statusChange = true;
            }
            if (pendingStatus.includes(status)) {
              unfinishedConversions = true;
            }
          }
        }));
      } catch (error) {
        console.log(error);
        return;
      }

      if (statusChange) {
        this.revision++;
      }

      if (unfinishedConversions) {
        clearTimeout(this.timeoutRefreshSessionUploads);
        this.timeoutRefreshSessionUploads = setTimeout(this.refreshStatusSessionUploads, constants.ONGOING_UPLOAD_REFRESH_INTERVAL);
      }
    },

    filesChange(files) {
      files.forEach(file => {
        if (!file.processed) {
          file.processed = true;
          this.dropFiles.push({
            file,
            uploading: false,
            progress: 0,
            uploadedFile: null, // null if upload not finished, false if upload failed, UploadedFile instance if upload successful
            cancelToken: null,
            // tus additions. Fields are ADDED, never replacing the tri-state above.
            sent: 0,            // bytes acknowledged by the server, for the absolute-bytes display
            resumable: false,   // true once a previous attempt's offset was picked up
            error: null,        // per-file message; replaces the old global error latch
            tusUpload: null     // tus.Upload handle, for abort()
          });
        }
      });
    },
    filesize(size) {
      return (size) ? filesize(size, {base: 10}) : null;
    },

    async startUpload(fileWrapper) {
      if (fileWrapper.uploading || fileWrapper.uploadedFile !== null) {
        return;
      }
      // Kill switch: when off, use the legacy single POST, which is the proven path.
      if (String(constants.TUS_ENABLED) === 'true') {
        return this.startResumableUpload(fileWrapper);
      }

      fileWrapper.uploading = true;
      fileWrapper.progress = 0;
      fileWrapper.cancelToken = axios.CancelToken.source();
      try {
        // 1. Get a PIMS upload ticket. ims-service resolves the tenant's storage + project
        //    from the active binding and signs the PIMS request — replacing the legacy per-user
        //    CYTOMINE signature (which the JWT-only edge no longer accepts).
        let slideId = `webui-${Date.now()}-${fileWrapper.file.name}`;
        let {data: ticket} = await pipelineService.createUploadTicket({
          slideId,
          slideMetadata: {filename: fileWrapper.file.name},
        });

        // 2. POST the file to the public PIMS upload endpoint with the ticket's query params
        //    (idStorage/idProject/lisUploadTicket/dateFull/…) and signed Authorization header.
        //    The HTTP Date header is forbidden in browsers, so PIMS reconstructs the signed token
        //    from the `dateFull` query param the ticket already carries.
        // Post to the exact upload URL the ticket hands back (authoritative public PIMS endpoint),
        // not a locally reconstructed one.
        let uploadUrl = ticket.uploadUrl || (this.uri);
        let query = new URLSearchParams(ticket.queryParams).toString();
        let formData = new FormData();
        formData.append('files[]', fileWrapper.file);

        // `Authorization` carries the CYTOMINE classic signature (validated by PIMS). The JWT-only
        // edge router reads the user JWT from the `X-User-Authorization` fallback slot (it strips
        // it before forwarding), so we send both.
        let token = await updateToken();
        let response = await axios.post(uploadUrl + '?' + query, formData, {
          headers: {
            authorization: ticket.headers.Authorization,
            'X-User-Authorization': `Bearer ${token}`,
          },
          onUploadProgress: progress => {
            fileWrapper.progress = Math.floor((progress.loaded * 100) / progress.total);
          },
          cancelToken: fileWrapper.cancelToken.token,
        });

        let result = (response.data || [])[0] || {};
        if (result.status && Number(result.status) >= 400) {
          throw new Error(result.error || 'Upload failed');
        }
        fileWrapper.uploadedFile = new UploadedFile(result.uploadedFile);
        this.refreshStatusSessionUploads();
        this.revision++;
      } catch (error) {
        if (!axios.isCancel(error)) {
          // Surface the failure instead of leaving the bar silently stuck at 0%.
          console.log('Upload failed', error);
          fileWrapper.uploadedFile = false;
          fileWrapper.error = this.describeError(error);
        }
      } finally {
        fileWrapper.uploading = false;
        this.pumpQueue();
      }
    },

    /**
     * Resumable upload via tus.
     *
     * An interrupted transfer continues from the server's byte offset instead of restarting,
     * which is what makes a ~100GB slide feasible: the edge caps any SINGLE request at one hour,
     * so a multi-hour upload can only ever succeed as many short requests.
     */
    async startResumableUpload(fileWrapper) {
      fileWrapper.uploading = true;
      fileWrapper.progress = 0;
      fileWrapper.sent = 0;
      fileWrapper.error = null;
      try {
        const slideId = `webui-${Date.now()}-${fileWrapper.file.name}`;
        const {data: ticket} = await pipelineService.createUploadTicket({
          slideId,
          slideMetadata: {filename: fileWrapper.file.name},
        });

        // The ticket's query params are replayed verbatim; only the path differs from legacy.
        const query = new URLSearchParams(ticket.queryParams).toString();
        const endpoint = `${constants.PIMS_URL}/upload/tus?${query}`;
        const retryDelays = String(constants.TUS_RETRY_DELAYS || '')
          .split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n));

        await new Promise((resolve, reject) => {
          const upload = new tus.Upload(fileWrapper.file, {
            endpoint,
            // MUST be set: the default is a single request, which would defeat the whole point.
            chunkSize: Number(constants.TUS_CHUNK_SIZE_BYTES) || 67108864,
            retryDelays: retryDelays.length ? retryDelays : [0, 3000, 6000, 12000, 24000],
            // Survive a tab close / browser restart: the user re-picks the same file and the
            // client matches the fingerprint against the stored upload URL.
            storeFingerprintForResuming: true,
            removeFingerprintOnSuccess: true,
            metadata: {filename: fileWrapper.file.name},
            headers: {authorization: ticket.headers.Authorization},
            // A 20-hour upload outlives any single access token, so the header is recomputed
            // per request rather than captured once for the whole transfer.
            onBeforeRequest: async (req) => {
              const token = await updateToken();
              req.setHeader('X-User-Authorization', `Bearer ${token}`);
            },
            onProgress: (sent, total) => {
              fileWrapper.sent = sent;
              fileWrapper.progress = total ? Math.floor((sent * 100) / total) : 0;
            },
            onError: reject,
            onSuccess: resolve,
          });
          fileWrapper.tusUpload = upload;
          // Resume from a previous attempt if this browser remembers one.
          upload.findPreviousUploads().then(previous => {
            if (previous.length) {
              fileWrapper.resumable = true;
              upload.resumeFromPreviousUpload(previous[0]);
            }
            upload.start();
          });
        });

        // The completing chunk returns promptly and does NOT block on the import, so the
        // UploadedFile is fetched from the status sub-resource and then polled as before.
        const uploadUrl = fileWrapper.tusUpload && fileWrapper.tusUpload.url;
        const {data: status} = await axios.get(`${uploadUrl}/status`);
        fileWrapper.uploadedFile = status.uploadedFile
          ? new UploadedFile(status.uploadedFile)
          : false;
        if (!status.uploadedFile) {
          fileWrapper.error = status.error || this.$t('upload-error');
        }
        this.refreshStatusSessionUploads();
        this.revision++;
      } catch (error) {
        console.log('Resumable upload failed', error);
        fileWrapper.uploadedFile = false;
        fileWrapper.error = this.describeError(error);
      } finally {
        fileWrapper.uploading = false;
        this.pumpQueue();
      }
    },

    describeError(error) {
      const status = error && error.response && error.response.status;
      if (status === 413) {
        return this.$t('upload-error-too-large');
      }
      if (status === 507) {
        return this.$t('upload-error-no-space');
      }
      return (error && error.message) || this.$t('upload-error');
    },
    startAll() {
      // Deliberately queued rather than parallel. At the bandwidth these slides are uploaded
      // over, running N at once means none of them finishes for days and every one is exposed
      // to interruption; serialising makes the first slide usable in hours.
      this.pumpQueue();
    },

    /** Start uploads up to the configured concurrency, then stop. Re-entrant. */
    pumpQueue() {
      const limit = Math.max(1, Number(constants.TUS_MAX_CONCURRENT_UPLOADS) || 1);
      const running = this.dropFiles.filter(w => w.uploading).length;
      const pending = this.dropFiles.filter(w => !w.uploading && w.uploadedFile === null);
      pending.slice(0, Math.max(0, limit - running)).forEach(w => this.startUpload(w));
    },

    cancelUpload(index) {
      let fileWrapper = this.dropFiles[index];
      if (fileWrapper.cancelToken) {
        fileWrapper.cancelToken.cancel();
      }
      if (fileWrapper.tusUpload) {
        // terminate: true issues DELETE, so the server frees the partial immediately instead of
        // holding up to 100GB until the reaper runs. On a big cancel the user wants the space back.
        fileWrapper.tusUpload.abort(true).catch(() => {});
      }
      this.dropFiles.splice(index, 1);
      this.pumpQueue();
    },
    cancelAll() {
      let nbFiles = this.dropFiles.length;
      let idx = 0;
      for (let i = 0; i < nbFiles; i++) {
        if (this.dropFiles[idx].uploadedFile !== null) {
          idx++;
        } else {
          this.cancelUpload(idx);
        }
      }
    },

    hideFinished() {
      let nbFiles = this.dropFiles.length;
      let idx = 0;
      for (let i = 0; i < nbFiles; i++) {
        let uploadedFile = this.dropFiles[idx].uploadedFile;
        if (uploadedFile !== null && this.finishedStatus.includes(uploadedFile.status)) {
          this.cancelUpload(idx);
        } else {
          idx++;
        }
      }
    },

    updatedTree() {
      this.revision++; // updating the table will result in new files objects => the uf details will also be updated
    },
  },
  activated() {
    this.fetchStorages();
    this.fetchProjects();
    this.fetchFormatInfos();
    this.refreshStorageStats();
    this.refreshStatusSessionUploads();
    this.tableRefreshInterval = constants.STORAGE_REFRESH_INTERVAL;
  },
  deactivated() {
    clearTimeout(this.timeoutRefreshSessionUploads);
    clearTimeout(this.timeoutRefreshStorageStats);
    this.tableRefreshInterval = 0;
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/colors.scss';

/* design tokens mapped from the handoff (styles.css :root) */
$ink: #0f172a;        // headings
$ink-2: #334155;      // labels
$muted: #64748b;
$muted-2: #94a3b8;
$line: #e6eaf0;
$line-2: #eef1f6;
$brand: $primary;     // ZAYA blue
$brand-ink: #1a5fc7;
$brand-tint: #eef4ff;
$brand-tint-2: #f6f9ff;

/* full-bleed white page split into a left rail + content pane.
   Fixed height (viewport − navbar) so the rail stays put and the content scrolls
   internally, matching the design's fixed-sidebar / scrolling-content behaviour. */
.imglib {
  display: flex;
  align-items: stretch;
  background: #fff;
  height: 100%;
  padding: 0;

  /* the app sets a global right-pad on fa icons; here spacing is gap-based */
  ::v-deep .fas, ::v-deep .far { padding-right: 0; }
}

/* ---------------------------------------------------------------- left rail */
.imglib-rail {
  width: 256px;
  flex: 0 0 256px;
  border-right: 1px solid $line;
  padding: 26px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  background: linear-gradient(180deg, #fcfdff, #ffffff 120px);
}
.imglib-rail-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $muted-2;
  padding: 2px 12px 10px;
}
.imglib-rail-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.imglib-rail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 600;
  color: $ink-2;
  text-align: left;
  transition: 0.14s;

  .icon {
    color: $muted;
    height: auto;
    width: auto;
    margin: 0;
  }
  &:hover { background: #f4f7fb; }
  &.active {
    background: $brand-tint;
    color: $brand-ink;
    .icon { color: $brand; }
    .imglib-rail-sub { color: $brand; }
  }
}
.imglib-rail-sub {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: 500;
  color: $muted-2;
}

/* storage-usage card */
.imglib-rail-foot {
  margin-top: auto;
  padding: 14px 12px 4px;
}
.usage-card {
  border: 1px solid $line;
  border-radius: 12px;
  padding: 13px 14px;
  background: #fff;
  // A flex child will not shrink below its content unless told to; without this the
  // card pushes past the rail instead of its text wrapping.
  min-width: 0;
  overflow-wrap: anywhere;
}
.usage-top {
  display: flex;
  // Label and figure sit side by side when they fit and the figure drops to its own
  // line when they do not. Previously both were `nowrap` inside a space-between row,
  // so at rail width the value simply overflowed the card.
  flex-wrap: wrap;
  align-items: baseline;
  gap: 1px 8px;
  margin-bottom: 9px;

  b {
    font-size: 12.5px;
    color: $ink-2;
    font-weight: 700;
    white-space: nowrap;
  }
  span {
    font-size: 11px;
    color: $muted-2;
    white-space: nowrap;
    // Keeps the figure hard right while it shares the line, and flush left once it
    // wraps -- no ragged middle state.
    margin-left: auto;
    // The value refreshes every 60s; proportional digits make it jitter as it changes.
    font-variant-numeric: tabular-nums;
  }
}
.usage-bar {
  height: 7px;
  border-radius: 99px;
  background: #eef1f6;
  overflow: hidden;
}
.usage-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #2f8df0, #1f6fe5);
}
.usage-foot {
  margin-top: 8px;
  font-size: 11px;
  color: $muted;
  // Translations of this string are noticeably longer than the English (de/ro run ~40%
  // over), so it has to be allowed to wrap rather than escape the card.
  line-height: 1.35;
  overflow-wrap: anywhere;
}

/* ------------------------------------------------------------------ content */
.imglib-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
}
/* left-aligned content (no centering) so there's no large gap between the rail and
   the page — the form stays a readable column, the storage table runs wide. */
.imglib-view {
  margin: 0;
  padding: 34px 40px 56px;
}
.imglib-view-upload { max-width: 900px; }
.imglib-view-storage { max-width: 1360px; }

.imglib-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}
.imglib-title {
  /* override the global h1 (centered, uppercase, padded) — design is left/sentence-case */
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-transform: none;
  text-align: left;
  padding: 0;
  color: $ink;
  line-height: 1.15;
  margin: 0 0 6px;
}
.imglib-subtitle {
  margin: 0;
  font-size: 14.5px;
  color: $muted;
}
.imglib-formats-btn {
  flex: 0 0 auto;
  margin-top: 4px;
  height: 34px;
  padding: 0 13px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid $line;
  background: #fff;
  color: $ink-2;
  .icon { color: $brand; }
  &:hover { border-color: #cdd6e4; background: #f8fafc; }
}

/* borderless stacked form (fields sit directly on the white surface) */
.imglib-card { background: transparent; }
.imglib-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 22px;
}
.imglib-field:last-child { margin-bottom: 0; }
.imglib-label {
  font-size: 13px;
  font-weight: 700;
  color: $ink-2;
  margin-bottom: 8px;
}
.imglib-optional {
  font-weight: 500;
  color: $muted-2;
}
.imglib-hint {
  margin-top: 8px;
  font-size: 12px;
  color: $muted-2;
}
.imglib-divider {
  height: 1px;
  background: $line-2;
  border: 0;
  margin: 24px 0;
}

/* multiselect → match the design's dd-trigger */
.imglib-field ::v-deep .multiselect {
  min-height: 46px;
  font-size: 14.5px;
}
.imglib-field ::v-deep .multiselect__tags {
  min-height: 46px;
  padding: 7px 40px 0 14px;
  border: 1px solid $line;
  border-radius: 11px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.imglib-field ::v-deep .multiselect__select { height: 44px; }
.imglib-field ::v-deep .multiselect__select::before { border-color: $muted transparent transparent; }
.imglib-field ::v-deep .multiselect__single,
.imglib-field ::v-deep .multiselect__placeholder,
.imglib-field ::v-deep .multiselect__input {
  padding-left: 0;
  margin-bottom: 0;
  line-height: 1.5;
  font-size: 14.5px;
  background: transparent;
}
.imglib-field ::v-deep .multiselect__single { color: $ink; }
.imglib-field ::v-deep .multiselect__placeholder { color: $muted-2; padding-top: 0; }
.imglib-field ::v-deep .multiselect__content-wrapper {
  border: 1px solid $line;
  border-radius: 12px;
  box-shadow: 0 14px 40px -10px rgba(15, 23, 42, 0.22);
  margin-top: 6px;
  overflow: hidden;
}
.imglib-field ::v-deep .multiselect--active .multiselect__tags {
  border-color: $brand;
  box-shadow: 0 0 0 3px rgba(31, 111, 229, 0.13);
}
.imglib-field ::v-deep .multiselect__tag {
  background: $brand-tint;
  color: $brand-ink;
  font-weight: 600;
  border-radius: 7px;
}
.imglib-field ::v-deep .multiselect__option--highlight {
  background: #f4f7fb;
  color: $brand-ink;
}
.imglib-field ::v-deep .multiselect__option--selected {
  background: #fff;
  color: $brand-ink;
  font-weight: 600;
}

/* dropzone */
.imglib-dropzone {
  display: block;
  width: 100%;

  ::v-deep .upload-draggable {
    width: 100%;
    border: 1.6px dashed #cdd8e8;
    border-radius: 14px;
    background: #fbfcfe;
    padding: 0;
    margin: 0;
    transition: 0.16s;
  }
  ::v-deep .upload-draggable:hover,
  ::v-deep .upload-draggable.is-hovered {
    border-color: $brand;
    background: $brand-tint-2;
  }
}
.imglib-drop-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 30px 16px;
}
.imglib-drop-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: $brand-tint;
  color: $brand;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  margin-bottom: 6px;
}
.imglib-drop-title {
  font-size: 15px;
  font-weight: 700;
  color: $ink;
  a { color: $brand; font-weight: 700; }
}
.imglib-drop-hint {
  font-size: 12.5px;
  color: $muted-2;
}

/* queued files */
.imglib-files {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.imglib-file {
  display: flex;
  align-items: center;
  gap: 13px;
  border: 1px solid $line;
  border-radius: 11px;
  padding: 11px 13px;
  background: #fff;
}
.imglib-file-icon {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f1f5f9;
  color: $muted;
  display: flex;
  align-items: center;
  justify-content: center;
}
.imglib-file-body { flex: 1; min-width: 0; }
.imglib-file-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.imglib-file-name {
  font-size: 13.5px;
  font-weight: 600;
  color: $ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.imglib-file-size {
  flex: 0 0 auto;
  font-size: 11.5px;
  color: $muted-2;
}
.imglib-file-progress {
  height: 5px;
  margin: 7px 0 0;
  ::v-deep .progress, &::-webkit-progress-bar { background: #eef1f6; }
}
.imglib-file-status { margin-top: 6px; }
.imglib-file-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
}

.imglib-overall {
  margin-top: 18px;
  height: 6px;
}

/* action bar */
.imglib-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 11px;
  margin-top: 24px;

  ::v-deep .upload .button { margin-bottom: 0; }
  .button {
    height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
  }
  .button.is-link {
    background: $brand;
    border-color: $brand;
    &:hover { background: $brand-ink; border-color: $brand-ink; }
  }
  .button.is-text {
    color: $brand;
    text-decoration: none;
    font-weight: 600;
    font-size: 13px;
    &:hover { color: $brand-ink; text-decoration: underline; background: transparent; }
  }
}

/* -------------------------------------------------------- formats modal */
.imglib-formats-card {
  width: 540px;
  max-width: 92vw;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}
.imglib-formats-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 24px 0;
}
.imglib-formats-ico {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: $brand-tint;
  color: $brand;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
}
.modal-card-body { padding: 14px 24px 26px; }
.imglib-formats-title {
  font-size: 18px;
  font-weight: 800;
  text-transform: none;
  letter-spacing: normal;
  color: $ink;
  margin: 6px 0 6px;
}
.imglib-formats-desc {
  font-size: 14px;
  color: $muted;
  margin-bottom: 16px;
}
.imglib-format-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 18px;
}
.imglib-format-tag {
  background: #fff;
  border: 1px solid #e3ebf8;
  border-radius: 7px;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 600;
  color: #3f5b86;
  white-space: nowrap;
  cursor: default;
}
.imglib-formats-notes {
  border-top: 1px solid $line-2;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    color: $muted;
    line-height: 1.5;
    .icon { color: $muted-2; flex: 0 0 auto; height: auto; }
  }
}

/* -------------------------------------------------------------- responsive */
@media screen and (max-width: 768px) {
  .imglib { flex-direction: column; height: auto; }
  .imglib-rail {
    flex-basis: auto;
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid $line;
    padding: 16px;
    overflow-y: visible;
  }
  .imglib-rail-nav { flex-direction: row; }
  .imglib-rail-item { width: auto; }
  .imglib-rail-foot { display: none; }
  .imglib-main { overflow-y: visible; }
  .imglib-view { padding: 24px 18px 40px; }
  .imglib-head { flex-direction: column; }
}
</style>
